import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from "react-router-dom";
import { FaCheck } from 'react-icons/fa';
import api from '../services/api';

const PlansSection = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.get('/subscription/plans/compare');
        if (response.data && response.data.length > 0) {
          const formattedPlans = response.data.map(plan => {
            const features = [];
            if (plan.features) {
                if (plan.features.profileViews === -1) features.push("Unlimited Profile Views");
                else if (plan.features.profileViews > 0) features.push(`${plan.features.profileViews} Profile Views`);

                if (plan.features.contactViewQuota === -1) features.push("Unlimited Contact Views");
                else if (plan.features.contactViewQuota > 0) features.push(`${plan.features.contactViewQuota} Contact Views`);

                if (plan.features.chatAccess) features.push("Full Chat Access");
                if (plan.features.verifiedBadge) features.push("Verified Profile Badge");
                if (plan.features.hideAds) features.push("Ad-Free Experience");
                if (plan.features.horoscopeMatching) features.push("Horoscope Matching");
                if (plan.features.jathagam) features.push("Jathagam Analysis");
                if (plan.features.marketAccess) features.push("Wedding Market Access");
                if (plan.features.advancedSearch) features.push("Advanced Search Filters");
            }
            
            // fallback if features are empty
            if (features.length === 0) {
                features.push("Create Profile", "View Profiles", "Send Interest");
            }

            return {
                name: plan.displayName || plan.name,
                price: plan.price?.monthly || "0",
                period: plan.price?.monthly === 0 ? "forever" : "monthly",
                features: features,
                cta: plan.price?.monthly === 0 ? "Get Started" : "Upgrade Now",
                popular: plan.popular || false
            };
          });
          setPlans(formattedPlans);
        }
      } catch (error) {
        console.error("Error fetching plans:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-rose-50/50 skew-x-12 transform translate-x-20"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-rose-600 font-bold tracking-wider uppercase text-sm">Membership Plans</span>
          <h2 className="text-4xl font-black text-gray-900 mt-2 mb-4">Simple, Transparent <span className="text-rose-600">Pricing</span></h2>
          <p className="text-gray-600 text-lg">Choose the plan that best fits your needs. No hidden fees.</p>
        </div>

        {loading ? (
            <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600"></div>
            </div>
        ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              viewport={{ once: true }}
              className={`relative p-8 rounded-3xl border ${plan.popular ? 'border-rose-500 bg-white shadow-2xl scale-105 z-10' : 'border-gray-200 bg-white shadow-lg hover:shadow-xl'} transition-all duration-300`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-rose-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                  Most Popular
                </div>
              )}
              <div className="text-center mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{plan.name}</h3>
                <div className="flex items-baseline justify-center">
                  <span className="text-4xl font-black text-gray-900">₹{plan.price}</span>
                  <span className="text-gray-500 ml-1">/{plan.period}</span>
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center text-gray-600">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 ${plan.popular ? 'bg-rose-100 text-rose-600' : 'bg-gray-100 text-gray-500'}`}>
                      <FaCheck className="text-xs" />
                    </div>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
  onClick={() => navigate("/login")}
  className={`w-full py-3 rounded-xl font-bold transition-all ${
    plan.popular
      ? "bg-rose-600 text-white hover:bg-rose-700 shadow-lg shadow-rose-500/30"
      : "bg-gray-100 text-gray-800 hover:bg-gray-200"
  }`}
>
  {plan.cta}
</button>
            </motion.div>
          ))}
        </div>
        )}
      </div>
    </section>
  );
};

export default PlansSection;